
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Leaf, Mail, Phone, MessageCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Index = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission - in production, this would send to your backend
    setTimeout(() => {
      toast({
        title: "Thank you for reaching out!",
        description: "We'll get back to you as soon as we relaunch in Spring 2026.",
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
      setIsSubmitting(false);
    }, 1000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="animate-fade-in w-full min-h-screen overflow-hidden bg-gradient-to-br from-white via-front-gray-50 to-front-gray-100">
      <div className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto">
          {/* Logo/Branding Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <Leaf className="w-10 h-10 text-front-purple" />
              <h2 className="text-3xl font-bold text-front-gray-900">
                Plant Autopilot<sup className="text-sm">®</sup>
              </h2>
            </div>
            <p className="text-sm text-front-gray-500 uppercase tracking-wider">
              Professional Plant Care Services
            </p>
          </div>

          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-24 h-24 mb-8 bg-front-purple/10 rounded-full">
              <Leaf className="w-12 h-12 text-front-purple" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-front-gray-900 leading-tight">
              We'll Be Back Soon! 🌱
            </h1>
            
            <p className="text-xl md:text-2xl text-front-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Plant Autopilot is taking a brief pause to make some exciting improvements and optimizations.
            </p>

            <div className="inline-block bg-front-lime/20 border-2 border-front-lime px-8 py-4 rounded-lg mb-8">
              <p className="text-lg font-semibold text-front-gray-900">
                Relaunching in <span className="text-front-purple">Spring 2026</span>
              </p>
            </div>

            <p className="text-lg text-front-gray-600 max-w-2xl mx-auto">
              We're working hard to bring you an even better plant care experience. Stay tuned for our return!
            </p>
          </div>

          {/* Contact Form Card */}
          <Card className="shadow-xl border-front-gray-200">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4 text-front-gray-900">
                  Have Questions?
                </h2>
                <p className="text-front-gray-600">
                  Leave your contact information and we'll get back to you when we relaunch.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-front-gray-700 mb-2">
                    Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-front-gray-700 mb-2">
                    <Mail className="inline w-4 h-4 mr-2" />
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-front-gray-700 mb-2">
                    <Phone className="inline w-4 h-4 mr-2" />
                    Phone (Optional)
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-front-gray-700 mb-2">
                    <MessageCircle className="inline w-4 h-4 mr-2" />
                    Message (Optional)
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Let us know what you're interested in or any questions you have..."
                    className="w-full min-h-[120px]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-front-purple hover:bg-front-purple-dark text-white py-6 text-lg font-medium"
                >
                  {isSubmitting ? "Sending..." : "Stay in Touch"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Footer Message */}
          <div className="text-center mt-12">
            <p className="text-front-gray-500">
              Thank you for your patience. Your plants will thank you when we return! 🌿
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
